import React from "react";
import { motion } from "framer-motion";
import { partners } from "../mock";

/**
 * Partners & Affiliations — institutional logo-mark grid.
 * Each partner is a clean bordered card with an abbreviated wordmark
 * on top and the full institutional name below.
 */
const PartnersSection = () => {
  return (
    <section
      id="partners"
      data-testid="partners-section"
      className="py-16 sm:py-20 px-4 sm:px-6 bg-[#FAEDE7] border-t border-gray-200"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-4 mb-10"
        >
          <span className="h-px w-10 bg-gray-900" />
          <span className="text-xs tracking-[0.25em] uppercase text-gray-600 font-medium">
            Partners & Affiliations
          </span>
          <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.28em] text-gray-400">
            Supported & Collaborative
          </span>
        </motion.div>

        <div className="space-y-10">
          <PartnerGroup
            label="Domestic · Republic of Korea"
            items={partners.domestic}
            delay={0.05}
          />
          <PartnerGroup
            label="International Collaborations"
            items={partners.international}
            delay={0.15}
          />
        </div>
      </div>
    </section>
  );
};

const PartnerGroup = ({ label, items, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    <div className="flex items-baseline justify-between mb-4">
      <div className="flex items-baseline gap-2">
        <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00BCD4]">
          §
        </span>
        <h3 className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-700">
          {label}
        </h3>
      </div>
      <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-400">
        {String(items.length).padStart(2, "0")}
      </span>
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
      {items.map((p, i) => (
        <PartnerCard key={p.abbr + i} partner={p} index={i} />
      ))}
    </div>
  </motion.div>
);

const PartnerCard = ({ partner, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-30px" }}
    transition={{
      duration: 0.4,
      delay: index * 0.04,
      ease: [0.22, 1, 0.36, 1],
    }}
    data-testid={`partner-${partner.abbr.toLowerCase()}`}
    className="group relative aspect-[5/3] bg-white border border-gray-200 hover:border-gray-900 transition-all duration-300 flex flex-col items-center justify-center px-3 cursor-default overflow-hidden"
  >
    {/* Top-left index */}
    <span className="absolute top-2 left-2 text-[9px] font-mono text-gray-300 tracking-[0.2em]">
      /{String(index + 1).padStart(2, "0")}
    </span>
    {/* Accent line on hover */}
    <span className="absolute top-0 left-0 h-px bg-[#00BCD4] w-0 group-hover:w-full transition-all duration-500 ease-out" />

    <div className="text-xl sm:text-2xl font-light tracking-tight text-gray-900 leading-none">
      {partner.abbr}
    </div>
    <div className="mt-1.5 text-[9px] font-mono uppercase tracking-[0.2em] text-gray-500 text-center leading-tight px-1">
      {partner.name}
    </div>
  </motion.div>
);

export default PartnersSection;
